package no.vareflow.app.domain

data class Product(
    val id: String = randomId(),
    val varenummer: String = "",
    val name: String,
    val unit: String,
    val pricePerUnit: Double,
    val category: String,
    val vat: Double = 15.0,
    val supplier: String = "",
    val importStatus: String = "Manuell",
    val dataQuality: String = "",
    val sourceRow: Int? = null,
    val sourceCategory: String = "",
    val aliases: List<String> = emptyList(),
    val active: Boolean = true,
    /** Subdivision inside the hovedvare, e.g. Vin → "Rødvin", "Hvitvin"; blank = none. */
    val undergruppe: String = ""
)

fun normalizeProductVat(rate: Double): Double = if (rate == 25.0) 25.0 else 15.0

/**
 * Order of products in a hovedvare before anyone has moved them by hand: as in the imported
 * spreadsheet (sourceRow, the same on every device), then the rest alphabetically.
 */
/** " rødvin " → "Rødvin": trimmed, single spaces, first letter capital. */
fun cleanUndergruppe(raw: String): String =
    raw.replace('\u00A0', ' ').trim().replace(Regex("\\s+"), " ").replaceFirstChar { it.uppercase() }

/**
 * Products of one hovedvare in sections by undergruppe, keeping their order: products without an
 * undergruppe first (no heading), then each undergruppe in the order it first appears.
 */
fun groupByUndergruppe(ordered: List<Product>): List<Pair<String, List<Product>>> {
    val groups = LinkedHashMap<String, MutableList<Product>>()
    val none = ordered.filter { it.undergruppe.isBlank() }
    ordered.filter { it.undergruppe.isNotBlank() }.forEach { p ->
        val key = groups.keys.firstOrNull { it.equals(p.undergruppe.trim(), ignoreCase = true) } ?: p.undergruppe.trim()
        groups.getOrPut(key) { mutableListOf() } += p
    }
    return (if (none.isNotEmpty()) listOf("" to none) else emptyList()) + groups.map { it.key to it.value.toList() }
}

/** The undergrupper used in a hovedvare, in the order they appear (for the edit dialog's list). */
fun undergrupperIn(products: List<Product>, category: String): List<String> =
    groupByUndergruppe(defaultProductOrder(products.filter { it.category == category })).map { it.first }.filter { it.isNotEmpty() }

fun defaultProductOrder(products: List<Product>): List<Product> =
    products.sortedWith { a, b ->
        val ra = a.sourceRow
        val rb = b.sourceRow
        when {
            ra != null && rb != null && ra != rb -> ra.compareTo(rb)
            ra != null && rb == null -> -1
            ra == null && rb != null -> 1
            else -> compareNorwegian(a.name, b.name)
        }
    }
